import * as domain from '@packages/domain'
import { O, pipe, RA, tt } from '@packages/prelude'
import * as React from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import { react } from '../commons'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ListAd } from '../Ad'
import { Raffle } from '../Raffle'
import { Podcast } from '../Podcast'
import { Placeholder } from '../Placeholder'
import { Post } from './Post'
import { Event } from './Event'

export interface PostListProps {
  fetcher: Generator
}

export const PostList: React.VFC<PostListProps> = ({ fetcher }) => {
  const [loadedPosts, setLoadedPosts] = React.useState<ReadonlyArray<domain.CombinedPost.Type>>([])
  const [hasMore, setHasMore] = React.useState<boolean>(true)

  const loadMore = React.useCallback(() => {
    const { value, done } = fetcher.next()
    setHasMore(!done)
    pipe(
      value,
      O.fromPredicate(tt.readonlyNonEmptyArray(domain.CombinedPost.Type).is),
      O.map(newPosts => setLoadedPosts(posts => [...posts, ...newPosts]))
    )
  }, [fetcher])

  React.useEffect(() => {
    setLoadedPosts([])
    loadMore()
  }, [loadMore])

  return (
    <InfiniteScroll pageStart={0} initialLoad={false} loadMore={loadMore} hasMore={hasMore} loader={<LoadingSpinner key={0} />}>
      {pipe(
        loadedPosts,
        RA.foldMap(react.monoid)(
          domain.CombinedPost.ADT.matchStrict({
            Post: post => <Post post={post} key={post.uri} />,
            Event: event => <Event post={event} key={event.uri} />,
            Ad: ad => <ListAd {...ad} key={ad.type} />,
            Raffle: raffle => <Raffle post={raffle} key={raffle.uri} />,
            Podcast: podcast => <Podcast post={podcast} key={podcast.uri} />,
            Placeholder: placeholder => <Placeholder {...placeholder} key={placeholder.type} />
          })
        )
      )}
    </InfiniteScroll>
  )
}
